/**
 * Permission Selector Component for Role Management
 * Displays categorized features and actions for selecting role permissions
 */

import React from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Feature, Action, CreateRoleRequest } from '@/types/rbac';
import type { MenuCategory } from '@/types/roles';

interface RolePermissionSelectorProps {
  categorizedFeatures: MenuCategory[];
  features: Feature[];
  actions: Map<string, Action[]>;
  formData: CreateRoleRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreateRoleRequest>>;
  expandedCategories: Set<string>;
  toggleCategory: (categoryId: string) => void;
  selectedFeatureId: string | null;
  setSelectedFeatureId: (id: string | null) => void;
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string | null) => void;
  handleToggleAllActionsForFeature: (featureId: string) => void;
  handleTogglePermission: (featureId: string, actionId?: string) => void;
  hasPermission: (featureId: string, actionId?: string) => boolean;
  isDisabled?: boolean;
  t: (key: string) => string;
}

const RolePermissionSelector: React.FC<RolePermissionSelectorProps> = ({
  categorizedFeatures,
  features,
  actions,
  formData,
  setFormData,
  expandedCategories,
  toggleCategory,
  selectedFeatureId,
  setSelectedFeatureId,
  selectedCategoryId,
  setSelectedCategoryId,
  handleToggleAllActionsForFeature,
  handleTogglePermission,
  hasPermission,
  isDisabled = false,
  t,
}) => {
  const normalizeId = (value: string | number | null | undefined): string =>
    value === null || value === undefined ? '' : String(value);

  const getFeatureActions = (feature: Feature | undefined | null, fallbackFeatureId?: string): Action[] => {
    const currentFeatureId = normalizeId(feature?.id ?? fallbackFeatureId);
    const directActions = actions.get(currentFeatureId) || [];
    if (directActions.length > 0) return directActions;

    // Fallback: samma namn (säkerhetsnät för äldre datamodeller)
    const featureName = feature?.name || '';
    if (featureName) {
      const sameNameFeature = features.find(
        (f) => normalizeId(f.id) !== currentFeatureId &&
               (f.name || '') === featureName &&
               (actions.get(normalizeId(f.id)) || []).length > 0
      );
      if (sameNameFeature) {
        return actions.get(normalizeId(sameNameFeature.id)) || [];
      }
    }

    return [];
  };

  const getDisplayActions = (feature: Feature | undefined | null, fallbackFeatureId?: string): Action[] => {
    const rawActions = getFeatureActions(feature, fallbackFeatureId);
    // "Visa" hanteras som default via feature-level permission (action_id = null).
    // Visa inte legacy "ACCESS/VIEW" som separat actionrad.
    return rawActions.filter((action) => {
      const code = String(action.code || '').toUpperCase();
      return code !== 'ACCESS' && code !== 'VIEW';
    });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-textDefault mb-3">
        {t('roles.menu')}
      </label>
      
      <div className="flex gap-4">
        {/* LEFT: Menu structure */}
        <div className="w-1/2 border border-border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
          {categorizedFeatures.map((category, categoryIndex) => {
            const isExpanded = expandedCategories.has(category.id);
            const categoryHasPermissions = category.features.some(f => 
              formData.permissions.some(p => p.feature_id === f.id)
            );
            const allCategoryFeaturesSelected = category.features.every(f =>
              formData.permissions.some(p => p.feature_id === f.id)
            );
            const selectedCount = category.features.filter(f => 
              formData.permissions.some(p => p.feature_id === f.id)
            ).length;

            return (
              <div key={category.id} className={categoryIndex > 0 ? 'border-t border-border' : ''}>
                {/* Category header */}
                <div 
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${
                    selectedCategoryId === category.id 
                      ? 'bg-blue-300 dark:bg-blue-700 border-l-4 border-l-blue-600 text-blue-900 dark:text-blue-100' 
                      : categoryHasPermissions 
                        ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100' 
                        : 'bg-bgElevated hover:bg-bgLight'
                  }`}
                  style={selectedCategoryId === category.id ? { backgroundColor: '#93c5fd', borderLeft: '4px solid #2563eb' } : {}}
                  onClick={() => { 
                    toggleCategory(category.id); 
                    setSelectedFeatureId(null); 
                    setSelectedCategoryId(category.id); 
                  }}
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-textSecondary" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-textSecondary" />
                    )}
                    <span className="font-medium text-sm text-textDefault">{category.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      selectedCount > 0
                        ? 'bg-green-500 text-white'
                        : 'bg-bgLight text-textTertiary'
                    }`}>
                      {selectedCount}/{category.features.length}
                    </span>
                  </div>
                  {!isDisabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (allCategoryFeaturesSelected) {
                          setFormData(prev => ({
                            ...prev,
                            permissions: prev.permissions.filter(
                              p => !category.features.some(f => f.id === p.feature_id)
                            ),
                          }));
                        } else {
                          const newPerms = category.features
                            .filter(f => !formData.permissions.some(p => p.feature_id === f.id))
                            .map(f => ({ feature_id: f.id }));
                          setFormData(prev => ({
                            ...prev,
                            permissions: [...prev.permissions, ...newPerms],
                          }));
                        }
                      }}
                      className="text-xs px-1.5 py-0.5 bg-bgLight rounded hover:bg-bgPrimary hover:text-textLight transition-colors"
                    >
                      {allCategoryFeaturesSelected ? 'Avmarkera' : 'Markera'}
                    </button>
                  )}
                </div>

                {/* Modules under category */}
                {isExpanded && (
                  <div className="bg-bgContainer">
                    {category.features.map((feature, featureIndex) => {
                      const featureActions = getDisplayActions(feature);
                      const hasFeaturePermission = formData.permissions.some(
                        p => normalizeId(p.feature_id) === normalizeId(feature.id)
                      );
                      const isSelected = normalizeId(selectedFeatureId) === normalizeId(feature.id);
                      const selectedActionCount = featureActions.filter(a => hasPermission(normalizeId(feature.id), normalizeId(a.id))).length;
                      const selectedCount = (hasFeaturePermission ? 1 : 0) + selectedActionCount;
                      const totalCount = featureActions.length + 1; // +1 för "Visa"

                      return (
                        <div 
                          key={feature.id} 
                          className={`flex items-center justify-between pl-8 pr-3 py-1.5 cursor-pointer transition-colors ${
                            featureIndex > 0 ? 'border-t border-border/30' : ''
                          } ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'hover:bg-bgLight'}`}
                          onClick={() => { setSelectedFeatureId(normalizeId(feature.id)); setSelectedCategoryId(category.id); }}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={hasFeaturePermission}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleAllActionsForFeature(feature.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              disabled={isDisabled}
                              className="w-4 h-4 rounded"
                            />
                            <span className={`text-sm ${hasFeaturePermission ? 'text-textDefault font-medium' : 'text-textSecondary'}`}>
                              {feature.name}
                            </span>
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            selectedCount > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-bgContainer text-textSecondary'
                          }`}>
                            {selectedCount}/{totalCount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT: Actions for selected module */}
        <div className="w-1/2 border border-border rounded-lg overflow-hidden">
          {selectedFeatureId ? (
            (() => {
              const selectedFeature = features.find(f => f.id === selectedFeatureId);
              const featureActions = getDisplayActions(selectedFeature, selectedFeatureId);
              const hasFeaturePermission = formData.permissions.some(
                p => normalizeId(p.feature_id) === normalizeId(selectedFeatureId)
              );

              return (
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="bg-bgElevated px-4 py-3 border-b border-border">
                    <h4 className="font-semibold text-textDefault">{selectedFeature?.name}</h4>
                    <p className="text-xs text-textTertiary mt-1">
                      {selectedFeature?.description || t('rolePermissions.selectFunctions')}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex-1 p-4 overflow-y-auto max-h-[400px]">
                    {!hasFeaturePermission ? (
                      <div className="text-center py-8 text-textTertiary">
                        <p className="text-sm">{t('rolePermissions.moduleNotEnabled')}</p>
                        {!isDisabled && (
                          <button
                            onClick={() => handleToggleAllActionsForFeature(selectedFeatureId)}
                            className="mt-2 text-xs px-3 py-1 bg-bgPrimary text-textLight rounded hover:opacity-90"
                          >
                            Aktivera modul
                          </button>
                        )}
                      </div>
                    ) : featureActions.length === 0 ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <label
                            className={`flex items-center gap-2 text-sm px-3 py-2 rounded cursor-pointer border transition-colors ${
                              hasFeaturePermission
                                ? 'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-600'
                                : 'bg-bgLight border-border hover:border-border'
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={hasFeaturePermission}
                              onChange={() => handleToggleAllActionsForFeature(selectedFeatureId)}
                              disabled={isDisabled}
                              className="w-4 h-4 rounded"
                            />
                            <span className={hasFeaturePermission ? 'text-green-700 dark:text-green-300' : 'text-textSecondary'}>
                              {t('common.view') || 'Visa'}
                            </span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm font-medium text-textDefault">Funktioner</span>
                          {!isDisabled && (
                            <button
                              onClick={() => {
                                const allActionsSelected = featureActions.every(a => hasPermission(selectedFeatureId, a.id));
                                const allSelected = hasFeaturePermission && allActionsSelected;
                                if (allSelected) {
                                  setFormData(prev => ({
                                    ...prev,
                                    permissions: prev.permissions.filter(
                                      p => p.feature_id !== selectedFeatureId
                                    ),
                                  }));
                                } else {
                                  const newPerms = featureActions
                                    .filter(a => !hasPermission(selectedFeatureId, a.id))
                                    .map(a => ({ feature_id: selectedFeatureId, action_id: a.id }));
                                  setFormData(prev => ({
                                    ...prev,
                                    permissions: [
                                      ...prev.permissions.filter(p => p.feature_id !== selectedFeatureId),
                                      { feature_id: selectedFeatureId }, // default "Visa"
                                      ...newPerms,
                                    ],
                                  }));
                                }
                              }}
                              className="text-xs px-2 py-1 bg-bgLight rounded hover:bg-bgPrimary hover:text-textLight transition-colors"
                            >
                              {(hasFeaturePermission && featureActions.every(a => hasPermission(selectedFeatureId, a.id))) ? 'Avmarkera alla' : 'Markera alla'}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <label
                            className={`flex items-center gap-2 text-sm px-3 py-2 rounded cursor-pointer border transition-colors ${
                              hasFeaturePermission
                                ? 'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-600'
                                : 'bg-bgLight border-border hover:border-border'
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={hasFeaturePermission}
                              onChange={() => handleToggleAllActionsForFeature(selectedFeatureId)}
                              disabled={isDisabled}
                              className="w-4 h-4 rounded"
                            />
                            <span className={hasFeaturePermission ? 'text-green-700 dark:text-green-300' : 'text-textSecondary'}>
                              {t('common.view') || 'Visa'}
                            </span>
                          </label>
                          {featureActions.map(action => {
                            const isChecked = hasPermission(selectedFeatureId, action.id);
                            return (
                              <label 
                                key={action.id} 
                                className={`flex items-center gap-2 text-sm px-3 py-2 rounded cursor-pointer border transition-colors ${
                                  isChecked
                                    ? 'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-600'
                                    : 'bg-bgLight border-border hover:border-border'
                                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleTogglePermission(selectedFeatureId, action.id)}
                                  disabled={isDisabled}
                                  className="w-4 h-4 rounded"
                                />
                                <span className={isChecked ? 'text-green-700 dark:text-green-300' : 'text-textSecondary'}>
                                  {action.name}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="h-full flex items-center justify-center text-textTertiary p-8">
              <div className="text-center">
                <ChevronRightIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('rolePermissions.selectModuleLeft')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolePermissionSelector;
